import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/api';
import { User } from '../types/user';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<any>;
    register: (email: string, password: string, firstName: string, lastName: string, middleName?: string, role?: 'user' | 'author') => Promise<void>;
    logout: () => void;
    updateUserAfterLogin: () => Promise<void>;
    setUserData: (userData: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (token) {
                try {
                    const response = await auth.getProfile();
                    setUser(response.data as any);
                } catch (error) {
                    const refreshToken = sessionStorage.getItem('refreshToken');
                    if (refreshToken) {
                        try {
                            const response = await auth.refreshToken(refreshToken);
                            if (response.data.token) {
                                sessionStorage.setItem('token', response.data.token);
                            }
                            if (response.data.refreshToken) {
                                sessionStorage.setItem('refreshToken', response.data.refreshToken);
                            }
                            const profileResponse = await auth.getProfile();
                            setUser(profileResponse.data as any);
                        } catch (refreshError) {
                            logout();
                        }
                    } else {
                        logout();
                    }
                }
            }
        } catch (error) {
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email: string, password: string) => {
        try {
            setError(null);
            console.log('Попытка входа с email:', email);
            const response: any = await auth.login({ email, password });
            console.log('Ответ сервера:', response.data);
            
            if (response.data.requiresCode) {
                return response;
            }
            
            if (!response.data.token) {
                throw new Error('Токен не получен от сервера');
            }
            
            if (response.data.token) {
                sessionStorage.setItem('token', response.data.token);
            }
            if (response.data.refreshToken) {
                sessionStorage.setItem('refreshToken', response.data.refreshToken);
            }
            
            if (!response.data.user) {
                throw new Error('Данные пользователя не получены от сервера');
            }
            
            console.log('Установка данных пользователя:', response.data.user);
            setUser(response.data.user as any);
            
            return response;
        } catch (error: any) {
            console.error('Ошибка при входе:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Ошибка входа';
            console.error('Сообщение об ошибке:', errorMessage);
            setError(errorMessage);
            return Promise.reject(error);
        }
    };

    // Функция для обновления пользователя после входа с кодом
    const updateUserAfterLogin = async () => {
        try {
            const response = await auth.getProfile();
            setUser(response.data as any);
        } catch (error) {
            console.error('Ошибка при обновлении пользователя:', error);
        }
    };

    const register = async (email: string, password: string, firstName: string, lastName: string, middleName?: string, role: 'user' | 'author' = 'user') => {
        try {
            setError(null);
            const response: any = await auth.register({ 
                email, 
                password, 
                firstName,
                lastName,
                middleName,
                role
            });
            if (response.data.token) {
                sessionStorage.setItem('token', response.data.token);
            }
            if (response.data.refreshToken) {
                sessionStorage.setItem('refreshToken', response.data.refreshToken);
            }
            if (response.data.user) {
                setUser(response.data.user as any);
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Ошибка регистрации');
            throw error;
        }
    };

    const logout = () => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
        setUser(null);
    };

    const setUserData = (userData: User) => {
        setUser(userData);
    };

    return (
        <AuthContext.Provider value={{ user, loading, error, login, register, logout, updateUserAfterLogin, setUserData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 